import {
    ClearinghouseApi,
    CoreFetch,
    ListProviderClearinghouseEraItemsRequest,
    ListProviderClearinghouseErasRequest,
    ListProviderClearinghousesRequest,
    ObjectIdResponse,
    ProviderClearinghouse,
    ProviderClearinghouseEra,
    ProviderClearinghouseEraItem,
} from '@enter/core';
import { toEndpointUnavailable } from './endpoint-unavailable';
import { EraScope, eraScope } from './era-scope';
import { IItemListing } from './root.store';
import { parseResponse } from './util';

export { EndpointUnavailableError } from './endpoint-unavailable';
export type { EraScope } from './era-scope';

/** What a failed read names on screen — the call and the query parameters it relies on, never their values. */
const ERAS_ENDPOINT = 'GET /provider/clearinghouse/era?filterClearinghouseId=';
const ERA_ITEMS_ENDPOINT = 'GET /provider/clearinghouse/era/item?filterClearinghouseId=&filterClearinghouseEraId=';

/** CLP02 claim status codes of the 835 5010 implementation guide, as a biller reads them. */
export const CLAIM_STATUS_LABELS: Record<string, string> = {
    '1': 'Processed as primary',
    '2': 'Processed as secondary',
    '3': 'Processed as tertiary',
    '4': 'Denied',
    '19': 'Processed as primary, forwarded',
    '20': 'Processed as secondary, forwarded',
    '21': 'Processed as tertiary, forwarded',
    '22': 'Reversal of previous payment',
    '23': 'Not our claim, forwarded',
    '25': 'Predetermination pricing only',
};

/**
 * What the list pages send — filters, paging and sort. The clearinghouse (and ERA) the list belongs to is
 * the store's to add, so the table can never override it.
 */
export type EraListRequest = Omit<ListProviderClearinghouseErasRequest, 'filterClearinghouseId'>;
export type EraItemListRequest = Omit<
    ListProviderClearinghouseEraItemsRequest,
    'filterClearinghouseId' | 'filterClearinghouseEraId'
>;

const listingFrom = <T>(response: Response, items: T[]): IItemListing<T> => ({
    items,
    limit: Number(response.headers.get('pagination-limit')),
    offset: Number(response.headers.get('pagination-offset')),
    total: Number(response.headers.get('pagination-total')),
});

/**
 * The account's own clearinghouses.
 *
 * Static, like the neighbouring {@link EraStore}: this is read-only lookup data with no
 * observable state worth holding, and the pages that use it drive a table presenter directly.
 *
 * Provider-scoped on purpose — the `/provider/*` endpoints are what keep an account to its own
 * data, so this must never reach for the richer `/support/*` equivalents (NS-12).
 */
export class ClearinghouseStore {
    /**
     * One page of clearinghouses. The body is a bare array, so the count comes from the
     * `pagination-total` header — the same way GuarantorService reads a listing total.
     */
    public static async list(request: ListProviderClearinghousesRequest): Promise<IItemListing<ProviderClearinghouse>> {
        const response = await CoreFetch<ClearinghouseApi>(ClearinghouseApi).listProviderClearinghousesRaw(request);

        return {
            items: await parseResponse(response),
            limit: Number(response.raw.headers.get('pagination-limit')),
            offset: Number(response.raw.headers.get('pagination-offset')),
            total: Number(response.raw.headers.get('pagination-total')),
        };
    }

    /**
     * One clearinghouse, used to title the drill-down. Callers should treat a rejection as
     * "name unknown" rather than an error: the name is decoration, the id in the URL is the fact.
     */
    public static async get(id: string): Promise<ProviderClearinghouse> {
        return CoreFetch<ClearinghouseApi>(ClearinghouseApi).getProviderClearinghouse({ id });
    }

    /**
     * Creates an ERA from an X12 835 (5010) document — `POST /provider/clearinghouse/{id}/era/x12/5010/835`,
     * which is in the generated client.
     *
     * `x12` is the file's text, sent verbatim as `application/edi-x12`: the generated runtime
     * passes string bodies through without JSON-encoding them. Not retried on failure — a retry
     * after a timeout the server survived would create a second ERA.
     */
    public static async uploadEra(clearinghouseId: string, x12: string): Promise<ObjectIdResponse> {
        return CoreFetch<ClearinghouseApi>(ClearinghouseApi).postProviderX12835Era({ id: clearinghouseId, body: x12 });
    }

    /**
     * One page of a clearinghouse's ERAs — `GET /provider/clearinghouse/era` filtered by clearinghouse.
     *
     * `scope` says whether the rows can be CONFIRMED as this clearinghouse's, from the `clearinghouseId`
     * each ERA carries: the filter was ignored on sandbox when it was first probed (2026-09-18), and a list
     * that quietly holds the whole account's ERAs would read as this clearinghouse's.
     * Throws EndpointUnavailableError when the endpoint answers with an error.
     */
    public static async listEras(
        clearinghouseId: string,
        request: EraListRequest,
    ): Promise<{ listing: IItemListing<ProviderClearinghouseEra>; scope: EraScope }> {
        try {
            const response = await CoreFetch<ClearinghouseApi>(ClearinghouseApi).listProviderClearinghouseErasRaw({
                ...request,
                filterClearinghouseId: [`EQ:${clearinghouseId}`],
            });
            const items = await response.value();

            return { listing: listingFrom(response.raw, items), scope: eraScope(items, clearinghouseId) };
        } catch (err) {
            throw await toEndpointUnavailable(err, ERAS_ENDPOINT);
        }
    }

    /** One ERA, to title the items level — `GET /provider/clearinghouse/era/{id}`. */
    public static async getEra(eraId: string): Promise<ProviderClearinghouseEra> {
        return CoreFetch<ClearinghouseApi>(ClearinghouseApi).getProviderClearinghouseEra({ id: eraId });
    }

    /**
     * One page of an ERA's claims — `GET /provider/clearinghouse/era/item`, filtered by both ids as the
     * support app sends them. Sandbox answered 500 `err.clearinghouse_era_does_not_exist` for every filter
     * when it was first probed (2026-09-18).
     * Throws EndpointUnavailableError when the endpoint answers with an error.
     */
    public static async listEraItems(
        clearinghouseId: string,
        eraId: string,
        request: EraItemListRequest,
    ): Promise<IItemListing<ProviderClearinghouseEraItem>> {
        try {
            const response = await CoreFetch<ClearinghouseApi>(ClearinghouseApi).listProviderClearinghouseEraItemsRaw({
                ...request,
                filterClearinghouseId: [`EQ:${clearinghouseId}`],
                filterClearinghouseEraId: [`EQ:${eraId}`],
            });

            return listingFrom(response.raw, await response.value());
        } catch (err) {
            throw await toEndpointUnavailable(err, ERA_ITEMS_ENDPOINT);
        }
    }
}

export default ClearinghouseStore;
